/** Scope 3 as a table page: Figures, Categories and Suppliers' share - ADR-0212 decisions 2, 3 and 10.
 *
 * **Why this is more than Scope 1 with a different number** (gate 19, ACC-09, S3-01). Scope 3 has
 * fifteen categories, each a decision, and a category nobody has looked at is not a category with
 * nothing in it. So the **Categories** tab is the assessment (`Scope3Categories.tsx`) and an
 * unassessed category says *Not assessed* and carries no quantity; the **Figures** tab lists the
 * calculated figures the engine can price today, each opening its receipt; and the **Suppliers'
 * share** tab says what suppliers' own answers account for (`Scope3Suppliers.tsx`).
 *
 * What the server offers, and what this page therefore does not do. There is no Scope 3 endpoint:
 * the decisions arrive on `GET /inventory` (`scope3`, all fifteen, always) and the figures on
 * `GET /figures?scope=3`. A figure carries the engine's category and *no Scope 3 category number*, so
 * it is **not** placed against a category - the mapping is a methodology fact with no verified row
 * behind it (rule 2) - and nothing is summed beyond the server's own total. The total tile says *Not
 * assessed* when the server says the scope is not, and never `0.0`.
 *
 * **A figure whose receipt cannot be produced is not shown** (rule 3): `splitByReceipt` withholds it
 * and the *Withheld figures* tile counts it, with the first broken link beside the count.
 *
 * **Add is for what the engine can price** (ADR-0212 decision 10): category 3's transmission and
 * distribution loss. The other fourteen categories have no manual path and say **Not available yet**
 * on the Categories tab; they are not hidden, and nothing is guessed into one.
 */

import { Truck } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../api/auth";
import { useFiguresPaged, useInventory } from "../api/queries";
import { splitByReceipt } from "../api/scope3";
import { ReadFailed } from "../components/Loaded";
import { WindowLockBanner } from "../components/WindowLockBanner";
import { AddImportActions, DashboardBand, EmptyAction, TablePage, Tile, TileStrip } from "../components/ui";
import { quantity, tonnes } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { mayEnterData } from "../shell/routeTable";
import { Scope3CategoryCard, Scope3GradeCard } from "./bands/Scope3Cards";
import { pagingOf } from "./drawerFigures";
import { RequirePeriod } from "./NoPeriod";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";
import type { Screen, Window as ScreenWindow } from "./useScreen";
import { useUrlTab } from "./useUrlTab";
import { Scope3Categories } from "./Scope3Categories";
import { Scope3Suppliers } from "./Scope3Suppliers";
import { AddFigureDrawer } from "./scopeTable/AddFigureDrawer";
import { useFiguresTable } from "./scopeTable/FiguresTable";
import { DASH } from "./scopeTable/context";
import type { Scope3Shared } from "./scopeTable/context";

const TABS = [
  { key: "figures", label: "Figures" },
  { key: "categories", label: "Categories" },
  { key: "suppliers", label: "Suppliers' share" },
] as const;

/** Spelled as `infoKey: "..."` so `infoText.test.ts`, which reads the source for the keys a component
 * names, finds both. */
const INFO = {
  figures: { infoKey: "scope3.figures" }, // gitleaks:allow (a text key, not a credential)
  withheld: { infoKey: "scope3.withheld" }, // gitleaks:allow (a text key, not a credential)
} as const;

function Scope3ScreenBody({ screen, period }: { screen: Screen; period: ScreenWindow }) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [tab] = useUrlTab(TABS);
  const [adding, setAdding] = useState(false);

  const inventoryRead = useInventory(period, screen.method);
  // The drawer behind the total tile: read when it is opened, one page at a time (`FIGURES_PAGE`).
  // A mark in the band opens a cut: a category's figures (`category-<engine category>`) or grade A's.
  const key = screen.breakdown ?? "";
  const categoryKey = /^category-(.+)$/.exec(key)?.[1];
  const breakdownRead = useFiguresPaged(screen.breakdown ? period : undefined, screen.method, {
    scope: "3",
    ...(categoryKey ? { category: categoryKey } : {}),
    ...(key === "grade-a" ? { grade: "A" as const } : {}),
  });
  const breakdownShown = breakdownRead.isPending || breakdownRead.error ? [] : breakdownRead.figures;

  const onAdd = () => setAdding(true);
  const onImport = () =>
    void navigate({ to: "/vendors", search: (prev) => ({ ...keepSearch(prev), tab: "requests" }) });

  const shared = { screen, period, role: me?.role, inventoryRead, onAdd, onImport };

  return (
    <ScreenFrame
      screen={screen}
      title="Scope 3"
      crumb="Value chain, by category"
      breakdownFigures={splitByReceipt(breakdownShown).shown}
      breakdownLabel={
        categoryKey ? `Scope 3 · ${categoryKey.replace(/_/g, " ")}` : key === "grade-a" ? "Scope 3 · grade A" : "Scope 3"
      }
      breakdownRead={breakdownRead}
      breakdownPaging={pagingOf(breakdownRead)}
    >
      {tab === "figures" ? (
        <Scope3Figures {...shared} />
      ) : tab === "categories" ? (
        <Scope3Categories {...shared} />
      ) : (
        <Scope3Suppliers {...shared} />
      )}
      <AddFigureDrawer scope="3" open={adding} onClose={() => setAdding(false)} />
    </ScreenFrame>
  );
}

function Scope3Figures({ screen, period, role, inventoryRead, onAdd, onImport }: Scope3Shared) {
  const navigate = useNavigate();
  const inventory = inventoryRead.data;
  const card = inventory?.totals_by_scope.find((c) => c.scope === "3");
  const coverage = inventory?.scope3 ?? null;
  const unread = inventoryRead.isPending || Boolean(inventoryRead.error);

  // **Withheld, counted by the server.** A figure whose evidence chain is incomplete is not listed here
  // (rule 3: such a figure is not shown), and the tile says how many and where the chain breaks. The
  // count is the server's count of those figures - the first page names the links, the total counts
  // them all - so it holds however many figures Scope 3 has.
  const withheldRead = useFiguresPaged(period, screen.method, { scope: "3", evidence: "broken" });
  const withheldTotal = withheldRead.isPending || withheldRead.error ? null : withheldRead.total;
  const firstBreaks = [...new Set(withheldRead.figures.map((f) => f.evidence_broken_at ?? "unknown"))];
  const moreBreaks = withheldRead.canLoadMore || withheldRead.ceilingReached;

  const grid = useFiguresTable({
    scope: "3",
    window: period,
    method: screen.method,
    serverTotalKg: card?.assessed ? card.kgco2e : undefined,
    carrierLabel: "Source",
    figureTestPrefix: "scope3-figure",
    // Only figures whose chain holds are listed, whatever else the reader filters by.
    fixedCut: { evidence: "complete" },
    onReceipt: screen.openReceipt,
    onOpenRecord: (id) =>
      void navigate({ to: "/records/$recordId", params: { recordId: id }, search: keepSearch }),
    emptyTitle: "No Scope 3 figures yet",
    emptyBody: "Purchases and supplier answers land here.",
    emptyIcon: Truck,
    emptyAction: (
      <EmptyAction
        label="Assess categories"
        allowed={role === undefined ? undefined : mayEnterData(role)}
        reason="A Contributor, Approver or Admin assesses categories."
        to="/scope-3"
        search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "categories" })}
        plus={false}
        testId="scope3-empty-assess"
      />
    ),
    emptyTestId: "scope3-no-figures",
  });
  const failure = grid.read.error ? (
    <ReadFailed
      of="The Scope 3 figures"
      error={grid.read.error}
      // One failed read, one notice, one retry (G-REG-01). The withheld count is `GET /figures` on
      // another cut and the filters are `GET /figures/facets`; each failed with it and has its notice
      // held back while this one shows, so the retry asks for every one that failed, or the first
      // press clears this notice and uncovers a second.
      onRetry={() => {
        grid.read.refetch();
        if (withheldRead.error) withheldRead.refetch();
        if (grid.facetsError) grid.refetchFacets();
      }}
    />
  ) : grid.facetsError ? (
    <ReadFailed
      of="The Scope 3 filters"
      error={grid.facetsError}
      onRetry={grid.refetchFacets}
    />
  ) : undefined;

  return (
    <TablePage
      icon={Truck}
      title="Scope 3"
      actions={<AddImportActions role={role} onAdd={onAdd} onImport={onImport} />}
      band={
        <>
          {/* Whether the window these figures are for is closed (ADR-0094): nothing for an open one. */}
          <WindowLockBanner lock={inventory?.window_lock} />
          {inventoryRead.error ? (
            <ReadFailed
              of="The Scope 3 totals"
              error={inventoryRead.error}
              onRetry={() => void inventoryRead.refetch()}
            />
          ) : null}
          <DashboardBand
            label="Scope 3 dashboard"
            charts={[
              <Scope3CategoryCard
                key="categories"
                read={inventoryRead}
                inventory={inventory}
                onOpen={(open) => screen.openBreakdown(open)}
              />,
              <Scope3GradeCard
                key="grades"
                read={inventoryRead}
                inventory={inventory}
                onOpen={(open) => screen.openBreakdown(open)}
              />,
            ]}
            tiles={
          <TileStrip
            label="Scope 3 summary"
            // Said only when the figures list itself did not fail: both are `GET /figures`, and one
            // cause said twice is two alerts for one problem.
            reads={grid.read.error ? [] : [{ of: "The count of withheld figures", read: withheldRead }]}
          >
            <Tile
              label="Scope 3 total"
              value={unread ? DASH : card?.assessed ? tonnes(card.kgco2e) : "Not assessed"}
              unit={!unread && card?.assessed ? "tCO₂e" : undefined}
              {...INFO.figures}
              onOpen={!unread && card?.assessed ? () => screen.openBreakdown("scope-3") : undefined}
              openLabel={card?.assessed ? `Scope 3 total: ${tonnes(card.kgco2e)} tonnes. Show the figures behind it` : undefined}
              loading={inventoryRead.isPending}
              testId="tile-total-3"
            />
            <Tile
              label="Categories assessed"
              value={unread || !coverage ? DASH : `${String(coverage.assessed)} of ${String(coverage.categories.length)}`}
              loading={inventoryRead.isPending}
              testId="tile-assessed"
            />
            <Tile
              label="Scope 3 figures"
              value={unread || !card ? DASH : quantity(String(card.figures))}
              loading={inventoryRead.isPending}
              testId="tile-figures"
            />
            <Tile
              label="Withheld figures"
              value={withheldTotal === null ? DASH : quantity(String(withheldTotal))}
              {...INFO.withheld}
              link={
                withheldTotal !== null && withheldTotal > 0 ? (
                  <span data-testid="scope3-withheld">
                    {quantity(String(withheldTotal))} not shown, incomplete evidence (first broken link:{" "}
                    {firstBreaks.join(", ")}
                    {moreBreaks ? " and others" : ""})
                  </span>
                ) : undefined
              }
              loading={withheldRead.isPending}
              testId="tile-withheld"
            />
          </TileStrip>
            }
          />
        </>
      }
      // Nothing to chart: the inventory answered with no Scope 3 figures, or could not be read. The
      // band's "No Scope 3 figures in this period" cards beside a failure notice claimed an empty
      // window that nobody had checked (G-P9-15).
      nothingYet={Boolean(inventoryRead.error) || (!unread && (card?.figures ?? 0) === 0)}
      filters={failure ? undefined : grid.filters}
      table={failure ?? grid.table}
    />
  );
}

/** Wrapped for the reason `Scope1Screen` gave: no unauthenticated surface exists, and without the
 * wrapper the page would render its empty state while every query 401s. */
export function Scope3Screen() {
  return (
    <RequireSession>
      <RequirePeriod
        title="Scope 3"
        crumb="Value chain, by category"
        tiles={["Scope 3 total", "Categories assessed", "Scope 3 figures", "Withheld figures"]}
      >
        {(screen, period) => <Scope3ScreenBody screen={screen} period={period} />}
      </RequirePeriod>
    </RequireSession>
  );
}
